import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Grid,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  Chip,
  Autocomplete,
  Divider
} from '@mui/material';
import {
  Add,
  Email,
  Message,
  Webhook,
  NotificationsActive,
  ArrowBack,
  ArrowForward,
  Check,
  Warning
} from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTheme } from '@mui/material/styles';
import { NotificationChannelType, AlertSeverity, Exporter } from '../types';
import { CANmailListSelector } from '../components/CANmailListSelector';

// ============================================================================
// Types
// ============================================================================

export interface AddNotificationChannelFormData {
  type: NotificationChannelType;
  name: string;
  // Type-specific config
  canmailListId?: number;
  email?: string;
  slackWebhookUrl?: string;
  slackChannel?: string;
  teamsWebhookUrl?: string;
  pagerdutyRoutingKey?: string;
  webhookUrl?: string;
  webhookSecret?: string;
  // Subscription settings
  watchAll: boolean;
  watchExporterIds: number[];
  severities: AlertSeverity[];
}

interface AddNotificationChannelDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: AddNotificationChannelFormData) => Promise<boolean>;
  exporters?: Exporter[];
}

// ============================================================================
// Constants
// ============================================================================

const CHANNEL_TYPES = [
  {
    type: 'canmail' as NotificationChannelType,
    label: 'CANmail List',
    description: 'Send to an existing CANmail distribution list',
    icon: Email,
    color: '#1976d2'
  },
  {
    type: 'email' as NotificationChannelType,
    label: 'Individual Email',
    description: 'Send to a single email address',
    icon: Email,
    color: '#388e3c'
  },
  {
    type: 'slack' as NotificationChannelType,
    label: 'Slack',
    description: 'Send to a Slack channel via webhook',
    icon: Message,
    color: '#611f69'
  },
  {
    type: 'teams' as NotificationChannelType,
    label: 'Microsoft Teams',
    description: 'Send to a Teams channel via webhook',
    icon: Message,
    color: '#464775'
  },
  {
    type: 'pagerduty' as NotificationChannelType,
    label: 'PagerDuty',
    description: 'Trigger PagerDuty incidents',
    icon: NotificationsActive,
    color: '#06ac38'
  },
  {
    type: 'webhook' as NotificationChannelType,
    label: 'Webhook',
    description: 'Send to a custom HTTP endpoint',
    icon: Webhook,
    color: '#f57c00'
  }
];

const STEPS = ['Select Channel Type', 'Configure Channel', 'Set Subscriptions'];

const SEVERITIES: { value: AlertSeverity; label: string; color: string }[] = [
  { value: 'critical', label: 'Critical', color: '#d32f2f' },
  { value: 'warning', label: 'Warning', color: '#ed6c02' },
  { value: 'info', label: 'Info', color: '#0288d1' }
];

// ============================================================================
// Component
// ============================================================================

export const AddNotificationChannelDialog: React.FC<AddNotificationChannelDialogProps> = ({
  open,
  onClose,
  onAdd,
  exporters = []
}) => {
  const theme = useTheme();

  // Step state
  const [activeStep, setActiveStep] = useState(0);

  // Form state - Step 1
  const [selectedType, setSelectedType] = useState<NotificationChannelType | null>(null);

  // Form state - Step 2
  const [name, setName] = useState('');
  const [canmailListId, setCanmailListId] = useState<number | ''>('');
  const [email, setEmail] = useState('');
  const [slackWebhookUrl, setSlackWebhookUrl] = useState('');
  const [slackChannel, setSlackChannel] = useState('');
  const [teamsWebhookUrl, setTeamsWebhookUrl] = useState('');
  const [pagerdutyRoutingKey, setPagerdutyRoutingKey] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');

  // Form state - Step 3
  const [watchAll, setWatchAll] = useState(true);
  const [watchExporterIds, setWatchExporterIds] = useState<number[]>([]);
  const [severities, setSeverities] = useState<AlertSeverity[]>(['critical', 'warning']);

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset form
  const resetForm = () => {
    setActiveStep(0);
    setSelectedType(null);
    setName('');
    setCanmailListId('');
    setEmail('');
    setSlackWebhookUrl('');
    setSlackChannel('');
    setTeamsWebhookUrl('');
    setPagerdutyRoutingKey('');
    setWebhookUrl('');
    setWebhookSecret('');
    setWatchAll(true);
    setWatchExporterIds([]);
    setSeverities(['critical', 'warning']);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Navigation
  const handleNext = () => {
    if (activeStep === 0 && !selectedType) {
      setError('Please select a channel type');
      return;
    }
    if (activeStep === 1 && !isStep2Valid()) {
      setError('Please fill in all required fields');
      return;
    }
    setError('');
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => prev - 1);
  };

  // Validation
  const isStep2Valid = () => {
    if (!name.trim()) return false;
    switch (selectedType) {
      case 'canmail':
        return !!canmailListId;
      case 'email':
        return !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      case 'slack':
        return !!slackWebhookUrl;
      case 'teams':
        return !!teamsWebhookUrl;
      case 'pagerduty':
        return !!pagerdutyRoutingKey;
      case 'webhook':
        return !!webhookUrl;
      default:
        return false;
    }
  };

  const isStep3Valid = () => {
    return severities.length > 0 && (watchAll || watchExporterIds.length > 0);
  };

  // Handle severity toggle
  const handleSeverityToggle = (severity: AlertSeverity) => {
    setSeverities((prev) =>
      prev.includes(severity)
        ? prev.filter((s) => s !== severity)
        : [...prev, severity]
    );
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!selectedType || !isStep3Valid()) return;

    setSubmitting(true);
    setError('');

    try {
      const formData: AddNotificationChannelFormData = {
        type: selectedType,
        name,
        canmailListId: selectedType === 'canmail' ? canmailListId as number : undefined,
        email: selectedType === 'email' ? email : undefined,
        slackWebhookUrl: selectedType === 'slack' ? slackWebhookUrl : undefined,
        slackChannel: selectedType === 'slack' ? slackChannel : undefined,
        teamsWebhookUrl: selectedType === 'teams' ? teamsWebhookUrl : undefined,
        pagerdutyRoutingKey: selectedType === 'pagerduty' ? pagerdutyRoutingKey : undefined,
        webhookUrl: selectedType === 'webhook' ? webhookUrl : undefined,
        webhookSecret: selectedType === 'webhook' ? webhookSecret : undefined,
        watchAll,
        watchExporterIds: watchAll ? [] : watchExporterIds,
        severities
      };

      const success = await onAdd(formData);
      if (success) {
        handleClose();
      } else {
        setError('Failed to add notification channel');
      }
    } catch (err) {
      setError('Failed to add notification channel');
      console.error('Add notification channel failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // Get channel type config
  const getSelectedTypeConfig = () => {
    return CHANNEL_TYPES.find((t) => t.type === selectedType);
  };

  // Render Step 1: Select Channel Type
  const renderStep1 = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose how you want to receive alert notifications:
      </Typography>

      <Grid container spacing={2}>
        {CHANNEL_TYPES.map((channel) => {
          const Icon = channel.icon;
          const isSelected = selectedType === channel.type;

          return (
            <Grid item xs={12} sm={6} key={channel.type}>
              <Paper
                variant={isSelected ? 'elevation' : 'outlined'}
                elevation={isSelected ? 3 : 0}
                onClick={() => {
                  setSelectedType(channel.type);
                  setError('');
                }}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: isSelected
                    ? `2px solid ${channel.color}`
                    : '1px solid #e0e0e0',
                  bgcolor: isSelected ? `${channel.color}10` : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: channel.color,
                    bgcolor: `${channel.color}05`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      bgcolor: channel.color,
                      mr: 2
                    }}
                  >
                    <Icon sx={{ color: 'white' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {channel.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {channel.description}
                    </Typography>
                  </Box>
                  {isSelected && (
                    <Check sx={{ color: channel.color }} />
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  // Render Step 2: Configure Channel
  const renderStep2 = () => {
    const typeConfig = getSelectedTypeConfig();

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          {typeConfig && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: typeConfig.color,
                mr: 1.5
              }}
            >
              <typeConfig.icon sx={{ color: 'white', fontSize: 18 }} />
            </Box>
          )}
          <Typography variant="subtitle1" fontWeight="medium">
            Configure {typeConfig?.label}
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="Channel Name *"
          placeholder="e.g., Engineering Team Alerts"
          value={name}
          onChange={(e) => setName(e.target.value)}
          helperText="A friendly name to identify this channel"
          sx={{ mb: 3 }}
        />

        {/* Type-specific fields */}
        {selectedType === 'canmail' && (
          <CANmailListSelector
            value={canmailListId === '' ? null : (canmailListId as number)}
            onChange={(listId) => setCanmailListId(listId ?? '')}
            required
            helperText="Select the CANmail distribution list to send alerts to"
          />
        )}

        {selectedType === 'email' && (
          <TextField
            fullWidth
            label="Email Address *"
            type="email"
            placeholder="alerts@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)}
            helperText="The email address to send alerts to"
          />
        )}

        {selectedType === 'slack' && (
          <>
            <TextField
              fullWidth
              label="Webhook URL *"
              placeholder="https://hooks.slack.com/services/..."
              value={slackWebhookUrl}
              onChange={(e) => setSlackWebhookUrl(e.target.value)}
              helperText="Incoming webhook URL from Slack"
              sx={{ mb: 3 }}
            />
            <TextField
              fullWidth
              label="Channel Name"
              placeholder="#alerts"
              value={slackChannel}
              onChange={(e) => setSlackChannel(e.target.value)}
              helperText="Optional - display name for the channel"
            />
          </>
        )}

        {selectedType === 'teams' && (
          <TextField
            fullWidth
            label="Webhook URL *"
            placeholder="https://outlook.office.com/webhook/..."
            value={teamsWebhookUrl}
            onChange={(e) => setTeamsWebhookUrl(e.target.value)}
            helperText="Incoming webhook URL from Microsoft Teams"
          />
        )}

        {selectedType === 'pagerduty' && (
          <TextField
            fullWidth
            label="Integration/Routing Key *"
            placeholder="32-character key"
            value={pagerdutyRoutingKey}
            onChange={(e) => setPagerdutyRoutingKey(e.target.value)}
            helperText="Events API v2 Integration Key from PagerDuty"
          />
        )}

        {selectedType === 'webhook' && (
          <>
            <TextField
              fullWidth
              label="Webhook URL *"
              placeholder="https://api.example.com/alerts"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              helperText="The URL to POST alert data to"
              sx={{ mb: 3 }}
            />
            <TextField
              fullWidth
              label="Secret (Optional)"
              placeholder="webhook-secret-for-signature"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              helperText="Used to sign webhook payloads for verification"
            />
          </>
        )}
      </Box>
    );
  };

  // Render Step 3: Subscription Settings
  const renderStep3 = () => (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure which alerts this channel should receive:
      </Typography>

      {/* Watch All Toggle */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={watchAll}
              onChange={(e) => setWatchAll(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="subtitle2">Watch All Exporters</Typography>
              <Typography variant="caption" color="text.secondary">
                Receive alerts from all current and future exporters
              </Typography>
            </Box>
          }
        />
      </Paper>

      {/* Exporter Selection (when not watching all) */}
      {!watchAll && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Select Exporters to Watch
          </Typography>
          <Autocomplete
            multiple
            options={exporters}
            getOptionLabel={(option) => option.displayName || option.name}
            value={exporters.filter((e) => watchExporterIds.includes(e.id))}
            onChange={(_, newValue) => {
              setWatchExporterIds(newValue.map((e) => e.id));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search exporters..."
                helperText={`${watchExporterIds.length} exporter(s) selected`}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.displayName || option.name}
                  size="small"
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
          />
          {!watchAll && watchExporterIds.length === 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Please select at least one exporter to watch
            </Alert>
          )}
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Severity Filter */}
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Alert Severities
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Only receive alerts matching these severity levels:
      </Typography>

      <FormGroup row>
        {SEVERITIES.map((severity) => (
          <FormControlLabel
            key={severity.value}
            control={
              <Checkbox
                checked={severities.includes(severity.value)}
                onChange={() => handleSeverityToggle(severity.value)}
                sx={{
                  color: severity.color,
                  '&.Mui-checked': { color: severity.color }
                }}
              />
            }
            label={
              <Chip
                label={severity.label}
                size="small"
                sx={{
                  bgcolor: severities.includes(severity.value)
                    ? `${severity.color}20`
                    : 'transparent',
                  border: `1px solid ${severity.color}`,
                  color: severity.color
                }}
              />
            }
          />
        ))}
      </FormGroup>

      {severities.length === 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Please select at least one severity level
        </Alert>
      )}

      {/* Summary */}
      <Paper
        variant="outlined"
        sx={{ p: 2, mt: 3, bgcolor: theme.palette.grey[50] }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Summary
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This channel will receive {severities.join(', ')} alerts from{' '}
          {watchAll
            ? 'all exporters'
            : `${watchExporterIds.length} selected exporter(s)`}
          .
        </Typography>
      </Paper>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '550px' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Add sx={{ mr: 1 }} />
          Add Notification Channel
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          {activeStep === 0 && renderStep1()}
          {activeStep === 1 && renderStep2()}
          {activeStep === 2 && renderStep3()}

          {/* Error display */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>

        {activeStep > 0 && (
          <Button
            onClick={handleBack}
            disabled={submitting}
            startIcon={<ArrowBack />}
          >
            Back
          </Button>
        )}

        {activeStep < STEPS.length - 1 ? (
          <Button
            onClick={handleNext}
            variant="contained"
            endIcon={<ArrowForward />}
          >
            Next
          </Button>
        ) : (
          <LoadingButton
            onClick={handleSubmit}
            variant="contained"
            disabled={!isStep3Valid()}
            loading={submitting}
            startIcon={<Add />}
          >
            Add Channel
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddNotificationChannelDialog;
